/*
 * Cart market swap
 *
 * Label products only exist in their own market's catalog (master handle for DE/AT, <master>-nl for
 * NL/BE, <master>-en for the other EU countries). When the market changes after products were added
 * (e.g. via the country selector or the shipping country in checkout), lines that are no longer sold
 * in the current market are replaced with the first candidate that is: <master> -> <master>-nl -> <master>-en.
 *
 * The check runs once per country (remembered in localStorage) and only when the cart has items.
 * Config is rendered by snippets/cart-market-swap.liquid.
 */
(() => {
  const STORAGE_KEY = 'tisso-cart-market';
  const NOTICE_CLASS = 'cart-market-swap-notice';
  const LABEL_SUFFIX_PATTERN = /-(nl|en)$/;

  const configElement = document.getElementById('CartMarketSwapConfig');
  if (!configElement || !window.fetch) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }
  if (!config.country) return;

  const marketKey = `${config.market || ''}:${config.country}`;

  const readStoredMarket = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const storeMarket = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, marketKey);
    } catch (error) {
      // Storage is unavailable (e.g. private mode): the check simply runs again on the next page load.
    }
  };

  if (readStoredMarket() === marketKey) return;

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const productCache = {};

  const normalize = (value) => String(value == null ? '' : value).trim().toLowerCase();

  // Resolves to the product JSON when the handle is sold in the current market, otherwise to null.
  // Throws on network errors so that an incomplete check is retried on the next page load.
  const fetchProduct = (handle) => {
    if (!productCache[handle]) {
      productCache[handle] = fetch(`${config.root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      })
        .then((response) => (response.ok ? response.json() : null))
        // A URL redirect can resolve to another product; only the requested handle counts.
        .then((product) => (product && normalize(product.handle) === handle ? product : null));
    }
    return productCache[handle];
  };

  const getMasterHandle = (line, lineConfig) => {
    let masterHandle = normalize(lineConfig && lineConfig.masterHandle);
    // A text metafield may hold a product URL instead of a plain handle.
    if (masterHandle.indexOf('/') !== -1) {
      masterHandle = masterHandle.replace(/[?#].*$/, '').replace(/\/+$/, '').split('/').pop();
    }
    return masterHandle || normalize(line.handle).replace(LABEL_SUFFIX_PATTERN, '');
  };

  const getCandidateHandles = (line, lineConfig) => {
    const currentHandle = normalize(line.handle);
    const masterHandle = getMasterHandle(line, lineConfig);
    return [masterHandle, `${masterHandle}-nl`, `${masterHandle}-en`].filter(
      (handle, index, list) => handle && handle !== currentHandle && list.indexOf(handle) === index
    );
  };

  const findVariant = (product, line, lineConfig) => {
    const variants = (product.variants || []).filter((variant) => variant.available);
    if (!variants.length) return null;

    const lineOptions = (line.variant_options || []).map(normalize);
    const byOptions = variants.find((variant) => {
      const options = (variant.options || []).map(normalize);
      return options.length === lineOptions.length && options.every((value, index) => value === lineOptions[index]);
    });
    if (byOptions) return byOptions;

    const position = lineConfig ? parseInt(lineConfig.variantPosition, 10) : 0;
    const byPosition = position > 0 ? product.variants[position - 1] : null;
    if (byPosition && byPosition.available) return byPosition;

    return variants[0];
  };

  // Returns the selling plan id for the new line, or null for a one-time purchase.
  const findSellingPlan = (product, variant, line) => {
    const allowedIds = (variant.selling_plan_allocations || []).map((allocation) => allocation.selling_plan_id);
    const plans = [];
    (product.selling_plan_groups || []).forEach((group) => {
      (group.selling_plans || []).forEach((plan) => {
        if (allowedIds.indexOf(plan.id) !== -1) plans.push(plan);
      });
    });

    const linePlan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    if (!linePlan) {
      return variant.requires_selling_plan && plans.length ? plans[0].id : null;
    }

    const optionKey = (plan) => (plan.options || []).map((option) => normalize(option.value)).join('|');
    const byName = plans.find((plan) => normalize(plan.name) === normalize(linePlan.name));
    if (byName) return byName.id;

    const lineOptionKey = optionKey(linePlan);
    const byOptions = lineOptionKey ? plans.find((plan) => optionKey(plan) === lineOptionKey) : null;
    if (byOptions) return byOptions.id;

    return variant.requires_selling_plan && plans.length ? plans[0].id : null;
  };

  // Resolves to { line, variant, sellingPlan, product } for a line that has to be swapped,
  // { line, unavailable: true } when no candidate is sold in this market, or null when nothing has to change.
  const resolveLine = (line) => {
    if (config.giftWrapProductId && line.product_id === config.giftWrapProductId) return Promise.resolve(null);

    const lineConfig = (config.lines || {})[line.key];
    const currentHandle = normalize(line.handle);

    return fetchProduct(currentHandle).then((currentProduct) => {
      if (currentProduct) return null;

      const candidates = getCandidateHandles(line, lineConfig);
      const tryCandidate = (index) => {
        if (index >= candidates.length) return Promise.resolve({ line, unavailable: true });
        return fetchProduct(candidates[index]).then((product) => {
          const variant = product ? findVariant(product, line, lineConfig) : null;
          if (!variant) return tryCandidate(index + 1);
          return { line, product, variant, sellingPlan: findSellingPlan(product, variant, line) };
        });
      };
      return tryCandidate(0);
    });
  };

  const postJson = (url, body) =>
    fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) }).then((response) => {
      if (!response.ok) throw new Error(`Cart request failed: ${url} (${response.status})`);
      return response.json();
    });

  const getSellingPlanId = (line) =>
    (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id) || null;

  // Line keys can change whenever the cart changes (e.g. when discount allocations are recalculated),
  // so the old line is looked up again by variant, selling plan and properties before it is removed.
  const findCurrentKey = (cart, line) => {
    const properties = JSON.stringify(line.properties || {});
    const match = (cart.items || []).find(
      (item) =>
        item.variant_id === line.variant_id &&
        getSellingPlanId(item) === getSellingPlanId(line) &&
        JSON.stringify(item.properties || {}) === properties
    );
    return match ? match.key : null;
  };

  // The new line is added before the old one is removed, so a failed add never loses the product.
  const swapLine = (swap) => {
    const item = {
      id: swap.variant.id,
      quantity: swap.line.quantity
    };
    if (swap.sellingPlan) item.selling_plan = swap.sellingPlan;
    if (swap.line.properties && Object.keys(swap.line.properties).length) item.properties = swap.line.properties;

    return postJson(`${config.cartAddUrl}.js`, { items: [item] })
      .then(() => fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } }))
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .then((cart) => {
        const key = findCurrentKey(cart, swap.line);
        if (!key) return null;
        return postJson(`${config.cartChangeUrl}.js`, { id: key, quantity: 0 });
      });
  };

  const formatMessage = (template, title) => (template || '').replace('[title]', title);

  const showNotices = (messages) => {
    if (!messages.length) return;
    document.querySelectorAll(`.${NOTICE_CLASS}`).forEach((element) => element.remove());

    // Placed next to (not inside) #AjaxCartForm, so it survives the cart re-render.
    document.querySelectorAll('cart-form#AjaxCartForm').forEach((cartForm) => {
      const container = document.createElement('div');
      container.className = NOTICE_CLASS;
      messages.forEach((text) => {
        const alert = document.createElement('p');
        alert.className = 'alert alert--error text-size--small';
        alert.setAttribute('role', 'status');
        alert.textContent = text;
        container.appendChild(alert);
      });
      cartForm.parentNode.insertBefore(container, cartForm);
    });
  };

  const refreshCartView = () => {
    if (typeof window.refreshCart === 'function') {
      window.refreshCart(false);
      return;
    }
    return fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        document.querySelectorAll('[data-header-cart-count]').forEach((element) => {
          element.textContent = cart.item_count;
        });
      });
  };

  const run = () =>
    fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .then((cart) => Promise.all((cart.items || []).map(resolveLine)))
      .then((results) => {
        const swaps = results.filter((result) => result && !result.unavailable);
        const unavailable = results.filter((result) => result && result.unavailable);
        const messages = [];
        let failed = false;

        // Swaps run one after another so every request works on an up-to-date cart.
        const swapChain = swaps.reduce(
          (chain, swap) =>
            chain.then(() =>
              swapLine(swap).catch((error) => {
                failed = true;
                console.error(error);
              })
            ),
          Promise.resolve()
        );

        return swapChain.then(() => {
          unavailable.forEach(({ line }) => {
            messages.push(formatMessage(config.locales.unavailable, line.product_title));
          });

          // Lines without a candidate keep being checked (and the notice shown) until they are removed.
          if (!failed && !unavailable.length) storeMarket();

          if (swaps.length) refreshCartView();
          showNotices(messages);
        });
      })
      .catch((error) => {
        console.error(error);
      });

  run();
})();
